import React, { useCallback, useEffect, useReducer } from 'react';
import { dayjs } from 'shared/utils/date';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';

import { Spin } from 'antd';
import QuestionResults from 'components/QuestionResults';
import ComparisonResult from 'components/QuestionResults/ComparisonResult';
import ChartHeader, { getTitle } from 'components/Charts/ChartHeader';
import ChartComment from 'components/Charts/ChartComment';
import ErrorBoundary from 'components/ErrorBoundary';
import { isPeriodsComparable } from 'shared/constants/date';

import messages from '../../../Reports/messages';
import './result-content.less';

const GET_CHART = gql`
  query($input: ResultInput) {
    result(input: $input)
  }
`;

const contentSettingsReducer = (state, action = {}) => ({ ...state, ...action });

const ResultContent = (props) => {
  const {
    translateTitle,
    surveyGroupId,
    displayedSurveyId,
    filterSegments,
    benchmarkSegments,
    chartSettings,
    onChartSettingsChanged,
    onContentSettingsChanged,
    viewDate,
    timeOffset,
    language,
    isReport,
    isPrint,
    wrapperClass = 'result-content',
    hideTitle,
    onEdit,
    allowPersonalData,
    onSelectChartSegment,
    dataSourceId,
    dateRange,
    canLoad = true,
    onLoaded,
  } = props;

  const { formatMessage: $t } = useIntl();

  const [contentSettings, dispatch] = useReducer(contentSettingsReducer, props.contentSettings);

  useEffect(() => {
    if (onContentSettingsChanged) {
      onContentSettingsChanged(contentSettings);
    }
  }, [contentSettings]);

  useEffect(() => {
    // We cant dispatch if there is also an onContentSettingsChanged since it creates an infinite loop
    if (!onContentSettingsChanged) {
      dispatch(props.contentSettings);
    }
  }, [props.contentSettings]);

  const { chartComment, ...settings } = contentSettings;
  const filter = { dateRange, surveyGroupId, filterSegments, benchmarkSegments, dataSourceId };

  const { loading, data, error, refetch } = useQuery(GET_CHART, {
    variables: { input: { viewDate, timeOffset, language, displayedSurveyId, filter, settings } },
    skip: !canLoad,
  });

  const waiting = !canLoad || loading;
  const title = getTitle(data?.result?.graph?.title || props.title, data?.result?.graph?.translateTitle || translateTitle);

  useEffect(() => {
    if (!waiting && (data || error) && onLoaded) {
      onLoaded();
    }
  }, [waiting, data, error, onLoaded]);

  const editableConfig = onContentSettingsChanged
    ? { onChange: value => dispatch({ chartComment: value }) }
    : false;

  let resultComponent;

  const addToReport = useCallback((args = {}) => {
    props.addToReport({
      contentSettings,
      title,
      ...args,
    });
  }, [props.addToReport, contentSettings, title]);

  const comparablePeriods = isPeriodsComparable(dayjs, dateRange);

  if (data?.result?.graph) {
    const { graph, periods, displayedSurvey } = data.result;
    if (graph.comparedTo) {
      const { question = {}, comparedTo = {}, segments = [] } = graph;

      resultComponent = (
        <ComparisonResult
          question={question}
          comparedTo={comparedTo}
          segments={segments}
          chartSettings={chartSettings}
          isPrint={isPrint}
          addToReport={props.addToReport && addToReport}
          onSelectChartSegment={onSelectChartSegment}
        />
      );
    } else {
      const compare = props.compare && !['FUNNEL', 'SCORE', 'META'].includes(graph.type) && (() => {
        props.compare({ ...graph, language: displayedSurvey?.language });
      });

      resultComponent = (
        <QuestionResults
          isReport={isReport}
          isPrint={isPrint}
          title={title}
          question={graph}
          periods={periods}
          surveyGroupId={surveyGroupId}
          displayedSurvey={displayedSurvey}
          chartSettings={chartSettings}
          onChartSettingsChanged={onChartSettingsChanged}
          contentSettings={contentSettings}
          onContentSettingsChanged={dispatch}
          addToReport={props.addToReport && addToReport}
          compare={compare}
          refetchChartResults={refetch}
          onEdit={onEdit && onEdit(contentSettings)}
          allowPersonalData={allowPersonalData}
          onSelectChartSegment={onSelectChartSegment}
          comparablePeriods={comparablePeriods}
        />
      );
    }
  } else {
    resultComponent = (
      <div style={{ minHeight: 382 }} />
    );
  }

  return (
    <div className={wrapperClass}>
      {!hideTitle && <ChartHeader title={title} />}
      <Spin
        size="large"
        spinning={waiting}
        tip={$t(messages.loadingChart)}
      >
        <ErrorBoundary>
          {resultComponent}
        </ErrorBoundary>
      </Spin>
      <ChartComment text={chartComment} editableConfig={editableConfig} />
    </div>
  );
};

export default ResultContent;
